<script setup lang="ts">
import type { ComputedRef, Ref } from "vue";
import { ref } from "vue";
import { SearchMode } from "~/core/composables/useSearch";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import { Filters } from "~/core/models/filters";
import type { DeviceTypeFilter } from "~/modules/dtwin/composables/useDeviceTypeFilters";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";

const { t } = useI18n();

const props = defineProps<{
  /*** Organization code */
  organizationCode?: number;
  /** Selected devices from the list */
  modelValue?: Dtwin[];
  /** Device types used as filter */
  types: DeviceTypeFilter[];
  /** Device declinations used as filter */
  declinations?: string[];
  /** Device name used as filter */
  name?: string;
}>();
const emit = defineEmits(["update:modelValue"]);

const page = ref(DEFAULT_FIRST_PAGE);
const pageSize = ref(DEFAULT_PAGE_SIZE);

/** Setup of the device source via composable usage. */
const { isLoading, results, fetchData, setFilter, search, onSearch } =
  useFetchDtwins({
    organizationCode: props.organizationCode,
    searchMode: SearchMode.FILTER_SEARCH,
  });

const filterMap: Ref<Map<Filters, any> | undefined> = ref();

async function loadData() {
  /*
   * If dtwin is used, every device fetch must be filtered using the selected
    types. In the current implementation only one type can be selected.
    */
  if (props.types.length > 0 && props.types[0].uids !== undefined) {
    setFilter(Filters.DTWIN_MODEL_TYPE, props.types[0].uids);
  }
  await fetchData(page.value, pageSize.value, new Map());
}

/**
 * Contains the fetched devices using the Device model as encapsulation.
 */
const devices: ComputedRef<Dtwin[]> = computed(() => {
  return (toRaw(results.value?.results) as Dtwin[] | undefined)
    ?.map((device: Dtwin) => {
      return {
        ...device,
        attributes: {
          ...device.attributes,
          // Get a name for the device in the list
          friendlyName:
            device.attributes?.friendlyName !== undefined &&
            device.attributes?.friendlyName !== ""
              ? device.attributes?.friendlyName
              : (device.attributes?.serialNumber ?? ""),
        },
      };
    })
    .filter((device: Dtwin) => {
      return (
        device.attributes?.friendlyName !== undefined ||
        (device.attributes?.friendlyName === "" &&
          device.attributes?.serialNumber !== undefined)
      );
    }) as Dtwin[];
});

const deviceNumber = computed(() => {
  return results.value?.pagination.totalCount;
});

// Apply provided default filters
const updateListFilter = () => {
  if (filterMap.value) {
    for (const [filter, value] of filterMap.value) {
      setFilter(filter, value ?? "");
    }
  }
};

watch(
  () => props.name,
  (filterByName) => {
    // The dtwin device can have no friendly name defined. The serial number is
    // used as fallback in this case.
    const filterType = Filters.DTWIN_NAME_OR_SERIAL_NUMBER;
    if (!filterMap.value) {
      filterMap.value = new Map<Filters, any>();
    }
    if (!filterByName) {
      if (filterMap.value.has(filterType)) {
        filterMap.value.set(filterType, "");
      }
    } else {
      filterMap.value.set(filterType, filterByName);
    }

    // When filtering by name, the list should be reset to it initial state.
    page.value = DEFAULT_FIRST_PAGE;
    updateListFilter();
    search();
  },
);

watch(
  () => props.types,
  (filterTypes) => {
    const typeValues = filterTypes.map((type) => type.value);
    if (!filterMap.value) {
      filterMap.value = new Map<Filters, any>();
    }
    if (!typeValues || typeValues.length === 0) {
      if (filterMap.value.has(Filters.MODEL_TYPE)) {
        filterMap.value.set(Filters.MODEL_TYPE, []);
      }
    } else {
      filterMap.value.set(Filters.MODEL_TYPE, typeValues);
    }
    page.value = DEFAULT_FIRST_PAGE;
    updateListFilter();
    search();
  },
);

watch(
  () => props.declinations,
  (filterValues) => {
    if (!filterMap.value) {
      filterMap.value = new Map<Filters, any>();
    }
    if (!filterValues || filterValues.length === 0) {
      if (filterMap.value.has(Filters.DTWIN_RANGE)) {
        filterMap.value.set(Filters.DTWIN_RANGE, []);
      }
    } else {
      filterMap.value.set(Filters.DTWIN_RANGE, filterValues);
    }

    page.value = DEFAULT_FIRST_PAGE;
    updateListFilter();
    search();
  },
);

const selectedItems = computed({
  get() {
    if (props.modelValue) {
      return props.modelValue.map((device) => device.uid ?? "");
    }

    return [];
  },
  set(selected?: string[]) {
    // Note: we are using destructuring assignment syntax here.
    if (selected != null) {
      const modelValue: (Dtwin | undefined)[] = selected
        .map((currentDeviceId) => {
          // If the device is not in the device list found, use modelValue where there was the previous selected device list
          let foundDevice: Dtwin | undefined = devices.value?.find(
            (currentDevice) => currentDevice.uid === currentDeviceId,
          );
          if (!foundDevice) {
            foundDevice = props.modelValue?.find(
              (currentDevice) => currentDevice.uid === currentDeviceId,
            );
          }
          return foundDevice;
        })
        .filter((currentDevice) => currentDevice !== undefined);
      emit("update:modelValue", modelValue);
    } else {
      emit("update:modelValue", []);
    }
  },
});

watch(() => page.value, loadData);

onSearch(loadData);
</script>

<template>
  <div class="pb-6 px-6">
    <div v-if="deviceNumber" class="flex justify-between mb-2">
      <span class="flex items-center font-medium">
        {{ t("dtwins.deviceFound", deviceNumber) }}
      </span>
      <pagination-buttons
        v-model:page="page"
        :page-size="pageSize"
        :total-count="deviceNumber"
      />
    </div>

    <filter-selectable-list
      v-model="selectedItems"
      :data="devices"
      :is-loading="isLoading"
      :empty-text="t('device.empty')"
      :header="t('logs.filters.entityClass.value.device')"
      header-class="underline text-base"
      prop="attributes.friendlyName"
      item-prop="uid"
    />
  </div>
</template>
